/**
 * Product card behaviors:
 *  - Color swatch click → swaps card image + variant URL.
 *
 * The wishlist heart that used to live here was removed: the Heartify app
 * embed injects its own heart into the same corner of every card and owns
 * the saved list server-side, so the theme's localStorage copy was both a
 * duplicate control and a second, diverging source of truth.
 */

document.addEventListener('click', (e) => {
  // ── Color swatch image swap ──
  const swatch = e.target.closest('[data-swatch]');
  if (!swatch) return;

  e.preventDefault();
  e.stopPropagation();

  const card = swatch.closest('[data-product-card]');
  if (!card) return;

  const img = card.querySelector('.product-card-image');
  if (img && swatch.dataset.imageSrc) {
    img.src = swatch.dataset.imageSrc;
    if (swatch.dataset.imageSrcset) {
      img.srcset = swatch.dataset.imageSrcset;
    }
  }

  if (swatch.dataset.url) {
    card.querySelectorAll('a').forEach((a) => {
      a.href = swatch.dataset.url;
    });
  }

  card.querySelectorAll('[data-swatch]').forEach((s) => {
    s.classList.remove('ring-2', 'ring-offset-1', 'ring-gray-900');
  });
  swatch.classList.add('ring-2', 'ring-offset-1', 'ring-gray-900');
});

/* ── Card image gallery ───────────────────────────────────────────────────
 * The track is a scroll-snap row, so touch swipe and trackpad scroll work
 * with no JS. These handlers only add the desktop arrow controls and keep
 * the dot indicator in sync. Each slide is its own <a>, and the arrows are
 * siblings of the track (not descendants of a link), so arrow clicks never
 * navigate to the product.
 */
const slideWidth = (track) => track.firstElementChild?.getBoundingClientRect().width || track.clientWidth;

const syncDots = (gallery) => {
  const track = gallery.querySelector('[data-card-track]');
  const dots = gallery.querySelectorAll('.pcard-dot');
  if (!track || !dots.length) return;

  const w = slideWidth(track);
  const index = w ? Math.round(track.scrollLeft / w) : 0;
  dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

  // Hide the arrow that would do nothing — a dead control reads as broken.
  const atStart = track.scrollLeft <= 1;
  const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
  gallery.querySelector('[data-card-prev]')?.toggleAttribute('hidden', atStart);
  gallery.querySelector('[data-card-next]')?.toggleAttribute('hidden', atEnd);
};

const initGalleries = (root = document) => {
  root.querySelectorAll('[data-card-gallery]').forEach((gallery) => {
    if (gallery.dataset.galleryReady) return;
    gallery.dataset.galleryReady = '1';

    const track = gallery.querySelector('[data-card-track]');
    if (!track) return;

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          syncDots(gallery);
          ticking = false;
        });
      },
      { passive: true }
    );

    syncDots(gallery);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initGalleries(), { once: true });
} else {
  initGalleries();
}

// Re-init for grids swapped in by the filter form or recommendations fetch.
document.addEventListener('collection:updated', () => initGalleries());
document.addEventListener('recommendations:loaded', () => initGalleries());
document.addEventListener('recently-viewed:loaded', () => initGalleries());
// <product-rail> duplicates its cards to loop; the copies need wiring too.
document.addEventListener('rail:cloned', () => initGalleries());

document.addEventListener('click', (e) => {
  const arrow = e.target.closest('[data-card-prev], [data-card-next]');
  if (!arrow) return;

  e.preventDefault();
  e.stopPropagation();

  const gallery = arrow.closest('[data-card-gallery]');
  const track = gallery?.querySelector('[data-card-track]');
  if (!track) return;

  const dir = arrow.hasAttribute('data-card-next') ? 1 : -1;
  track.scrollBy({ left: dir * slideWidth(track), behavior: 'smooth' });
});
